import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getCompaniesName,
    getAllProjects as getAllProjectsSuperAdmin
} from '../../services/api/implementation/impl';
import {
    getAllProjects,
    getCloseDeals
} from '../../../builder/services/api/implementation/impl';

import Layout from './AppLayout';
import Header from '../../../shared/components/layout/Header';
import GridWrapper from '../../../shared/components/layout/GridWrapper';
import Table from '../../../shared/components/layout/Table';
import Search from '../../../shared/components/ui/Search';
import { transformData } from '../../utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

const BuilderCompanies = () => {
    const { builderId } = useParams();
    // console.log('BuilderCompanies loaded with builderId:', builderId);
    const userId = Cookies.get('userId');
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);

    // Fetch all companies
    const { data: companiesList, isFetching: isFetchingCompanies } = useQuery({
        queryKey: ['allCompaniesList'],
        queryFn: () => getCompaniesName(),
        onError: (err) => {
            console.error('Error fetching companies list:', err);
        }
    });

    // Fetch all projects
    const { data: projects, isFetching: isFetchingProjects } = useQuery({
        queryKey: ['allProjects'],
        queryFn: () => getAllProjects(),
        onError: (err) => {
            console.error('Error fetching projects:', err);
        }
    });

    // Fetch all closed deals
    const { data: closedDealsData, isFetching: isFetchingDeals } = useQuery({
        queryKey: ['allClosedDeals'],
        queryFn: () => getCloseDeals(null, 1, 9999),
        onError: (err) => {
            console.error('Error fetching closed deals:', err);
        }
    });

    // Process data for the table
    const processedData = useMemo(() => {
        if (!companiesList || !projects || !closedDealsData?.data || !builderId) return [];

        // Filter companies for this builder
        const builderCompanies = companiesList.filter(c => c.builder_id === parseInt(builderId) || c.builder_id == builderId);

        return builderCompanies.map((company) => {
            // Count projects matching this company_id
            const companyProjects = projects.filter((p) => p.company_id === company.id);
            const projectsCount = companyProjects.length;

            // Count closed deals for this company
            const closedDealsCount = closedDealsData.data.filter((deal) =>
                deal?.unit?.project?.company_id === company.id
            ).length;

            return {
                id: company.id,
                company_name: company.company_name || 'N/A',
                projects_count: projectsCount,
                closed_deals_count: closedDealsCount
            };
        });
    }, [companiesList, projects, closedDealsData, builderId]);

    // Handle search
    const handleSearchChange = useCallback(
        (incomingQuery) => {
            setSearchQuery(incomingQuery);

            if (incomingQuery) {
                const lowerCaseQuery = incomingQuery.toLowerCase();
                const filtered = processedData.filter(
                    (company) =>
                        company.company_name?.toLowerCase().includes(lowerCaseQuery)
                );
                setFilteredData(filtered || []);
            } else {
                setFilteredData(processedData || []);
            }
        },
        [processedData]
    );

    // Update filteredData when processedData changes
    useEffect(() => {
        if (!searchQuery) {
            setFilteredData(processedData);
        }
    }, [processedData, searchQuery]);

    // Transform for table consumption
    const tableDataFormat = ['id', 'company_name', 'projects_count', 'closed_deals_count'];
    const transformedData = filteredData.map((el) =>
        transformData(el, tableDataFormat)
    );

    const isLoading = isFetchingCompanies || isFetchingProjects || isFetchingDeals;

    const handleRowClick = (companyId) => {
        // Find the company to check project count
        const company = filteredData.find(c => c.id === companyId);

        if (company && company?.projects_count > 0) {
            navigate(`/super-admin/closed-deals/${builderId}/companies/${companyId}/projects`);
        } else {
            toast.error('No projects found for this company');
        }
    };

    return (
        <Layout>
            {/* Top Header */}
            <div className="fixed z-[20] top-0 left-[16.5vw] w-[83.5vw]">
                <Header data={{ name: 'Builder Companies' }} backArrow={true} />
            </div>

            {/* Search */}
            <div className="flex items-center justify-between px-6 py-4 bg-white fixed top-[4rem] z-20 border-t-[2px] border-stone-100 w-[83vw] shadow-sm">
                <Search query={searchQuery} onChange={handleSearchChange} />
            </div>

            {/* Main Content - Table */}
            <GridWrapper width={100}>
                <div className="mt-[10rem] relative user-table-container h-[calc(100vh-11.5rem)] overflow-auto w-full">
                    <style>{`
              .user-table-container .table-body {
                position: static !important;
                overflow-y: visible !important;
                max-height: none !important;
                height: auto !important;
                top: auto !important;
                padding-bottom: 0 !important;
              }
              .user-table-container .table-container {
                height: auto !important;
              }
              .user-table-container > div {
                height: auto !important;
              }
            `}</style>

                    {isLoading ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <Table
                            headers={['S.No', 'Company Name', 'Number of Projects', 'Number of Closed Deals']}
                            data={transformedData.map((item, index) => ({
                                serialNumber: index + 1,
                                ...item
                            }))}
                            identifier={'id'}
                            handleRowClick={handleRowClick}
                        />
                    )}
                </div>
            </GridWrapper>
        </Layout>
    );
};

export default BuilderCompanies;
